import React, { useEffect, useState, useMemo } from "react"
import { DateTime } from "luxon"
import PropTypes from "prop-types"
import Card from "../../../../components/atoms/Card.atom"
import {
  ValueWithLabelContainer,
} from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import { VacationDaysValueTemplate } from "../common/VacationDaysValueTemplate.controller"
import {
  VACATION_DAY_TYPE_NAMES,
  VACATION_DAY_TYPE_COPYS,
  IDS_HOLIDAY_SCHEMA,
  VACATION_DAY_TYPES,
  VACATION_DAYS_VARIANTS,
} from "../../../../consts/modules/vacations.consts"
import {
  getTeamMemberVacationResume,
  getTeamMemberTypesDayOff,
  getTeamMemberTypeDayOffInfo,
} from "../../../../services/vacation.service"
import { ReactComponent as CalendarClockIcon } from "../../../../design/assests/images/icons/calendar_clock.svg"
import { hasAvailableVacationDays, getAdditionalDaysSummary, getTeamMembersStartOfPeriodMonth, getTeamMembersEndOfPeriodMonth } from "../../../../utils/vacation.utils"
import ModalForm from "../../../../components/compounds/modalForm/ModalForm.compunds"
import TeamMemberVacationsBucketTable from "../common/TeamMemberVacationsBucketTable.controller"
import TeamMemberVacationsAditionalTable from "../common/TeamMemberVacationsAditionalTable.controller"
import { BREAKPOINTS } from "../../../../consts/responsiveBreakpoints.consts"
import { ReactComponent as BucketIcon } from "../../../../design/assests/images/icons/glass_cup.svg"
import useTeamMemberTakenDaysResume from "../common/hooks/useTeamMemberTakenDaysResume.hook"
import GlassCupIcon from "../../../../design/assests/images/icons/glass-cup"
import useThemeContext from "../../../../hooks/contextConsumers/useThemeContext.hook"
import { ReactComponent as BeachAccessIcon } from "../../../../design/assests/images/icons/beach_access.svg"
import VacationDayVariantChip from "../../../../components/molecules/VacationDayVariantChip.molecule"

  const plusDayMessages = [
      { threshold: 1, text: "Faltan menos de 1 mes para el vencimiento de tus días+" },
      { threshold: 2, text: "Faltan menos de 2 meses para el vencimiento de tus días+" },
      { threshold: 3, text: "Faltan menos de 3 meses para el vencimiento de tus días+" },
    ]

const TeamMemberVacationsResume = ({
  periodRange = [
    new DateTime().now().startOf("year"),
    new DateTime().now().endOf("year"),
  ],
  setLoading: setGlobalLoading = () => {},
  refresh,
  setHasAvailableVacationDays,
  holidaySchemaId = undefined,
  teamMemberID = undefined,
  workAnniversary = undefined,
  screenWidth = undefined
}) => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({
    usedVacationDays: 0,
    availableBucketDays: 0,
    availablePlusDays: 0,
    availableAccordedDays: 0,
    availableAdditionalDays: 0,
  })
  const { theme } = useThemeContext()
  const [typesDayOff, setTypesDayOff] = useState([])
  const [plusDaysMessage, setPlusDaysMessage] = useState([])
  const [additionalDaysMessages, setAdditionalDaysMessages] = useState([])
  const [isModalBucketResumeVisible, setIsModalBucketResumeVisible] = useState(false)
  const [isModalAditionalResumeVisible, setIsModalAditionalResumeVisible] = useState(false)
  const takenDaysFilters = useMemo(() => {
    const startDate = periodRange[0].toISODate()
    const endDate = periodRange[1].toISODate()
    return {
      startDate,
      endDate,
      teamMemberID,
    }
  }, [periodRange, refresh])
  
  const {takenDaysResume, isLoading: isTakenDaysLoading} = useTeamMemberTakenDaysResume(takenDaysFilters)
  const modalBucketResumeVisibleToggle = () => setIsModalBucketResumeVisible(!isModalBucketResumeVisible)
  const modalAditionalResumeVisibleToggle = () => setIsModalAditionalResumeVisible(!isModalAditionalResumeVisible)

  useEffect(() => {
    fetchTypesDayOff()
  }, [])

  const fetchTypesDayOff = async () => {
    try {
      const response = await getTeamMemberTypesDayOff()
      if (!response.success) {
        throw new Error(response.message)
      }
      const result = response.result
      setTypesDayOff(result)
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  useEffect(() => {
    fetchData()
    fetchTeamMemberTypeDayOff()
  }, [periodRange, refresh])

  const fetchData = async () => {
    setLoading(true)
    setGlobalLoading(true)
    try {
      const today = DateTime.now()
      const response = await getTeamMemberVacationResume(
        today.startOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        }),
        today.endOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        }),
        getTeamMembersStartOfPeriodMonth(workAnniversary, periodRange[0]),
        getTeamMembersEndOfPeriodMonth(workAnniversary, periodRange[1]),
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      const result = response.result
      setHasAvailableVacationDays(hasAvailableVacationDays(result))
      setData(result)
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
      setGlobalLoading(false)
    }
  }

  const fetchTeamMemberTypeDayOff = async () => {
    try {
      const response = await getTeamMemberTypeDayOffInfo(
        getTeamMembersStartOfPeriodMonth(workAnniversary, periodRange[0]),
        getTeamMembersEndOfPeriodMonth(workAnniversary, periodRange[1])
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      const result = response.result
      setPlusDaysMessage(
        result.filter((dayOff) => dayOff.type === VACATION_DAY_TYPE_NAMES.PLUS)
      )
      setAdditionalDaysMessages(
        result.filter((dayOff) => dayOff.type === VACATION_DAY_TYPE_NAMES.EXTRA)
      )
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const parseDateOnlyFromISO = (isoDateString) => {
    const dateOnly = isoDateString.split('T')[0]
    return DateTime.fromFormat(dateOnly, 'yyyy-MM-dd')
  }

  const isPlusDaysPackageAboutToExpire = (dayMessages) => {
    if (dayMessages.length > 0) {
      const validMessages = dayMessages.filter(msg => msg.availableDays > 0)
      if (validMessages.length === 0) return false
      
      const earliestExpirationDate = validMessages.reduce((earliest, current) => {
      const currentDate = parseDateOnlyFromISO(current.expirationDate)
        return currentDate < earliest ? currentDate : earliest
      }, parseDateOnlyFromISO(validMessages[0].expirationDate))
      const monthsBeforeExpirationDate = earliestExpirationDate.diffNow("months").months
      if (monthsBeforeExpirationDate >= 3) return false
      else return true
    } 
    return false
  }


  const generatePlusDaysMessage = (dayMessages) => {
    if(dayMessages.length === 0) return null
    const validDayMessages = dayMessages.filter(msg => msg.availableDays > 0)
    .sort((a,b) => {
      const dateA = parseDateOnlyFromISO(a.expirationDate)
      const dateB = parseDateOnlyFromISO(b.expirationDate)
      return dateA.toMillis() - dateB.toMillis()
    })
    if(validDayMessages.length === 0) return null
    else if(validDayMessages.length === 1) {
      const expirationDate = DateTime.fromFormat(validDayMessages[0].expirationDate.split('T')[0], 'yyyy-MM-dd').toFormat("dd/LL/yyyy")
      const monthsBeforeExpiration = DateTime.fromFormat(validDayMessages[0].expirationDate.split('T')[0], 'yyyy-MM-dd').diffNow("months").months
      const message = plusDayMessages.find(m => monthsBeforeExpiration < m.threshold)
      if(!message){
    return (
        <div>
          <p>{`Fecha de vencimiento: ${expirationDate}`}</p>
          <p>Recuerda solicitar tus Días+ con al menos 3 meses de anticipación.</p>
        </div>
      )
    }
    return (
      <div className="additionalDaysMessagesColumn flex flex-column">
        <div className="additionalDaysMessagesColumn__message flex flex-column">
          {message && <span>{message.text}</span>}
          <span>
            {`Fecha de vencimiento: ${expirationDate}`}
          </span>
        </div>
      </div>
    )
    } else if(validDayMessages.length === 2) {
      const firstExpirationDate = DateTime.fromFormat(validDayMessages[0].expirationDate.split('T')[0], 'yyyy-MM-dd').toFormat("dd/LL/yyyy")
      const secondExpirationDate = DateTime.fromFormat(validDayMessages[1].expirationDate.split('T')[0], 'yyyy-MM-dd').toFormat("dd/LL/yyyy")
      const firstYear = parseDateOnlyFromISO(validDayMessages[0].expirationDate).year
      const secondYear = parseDateOnlyFromISO(validDayMessages[1].expirationDate).year
      return (
        <div className="flex flex-column gap-1">
          <span>Periodo: {firstYear} - {firstYear - 1}</span>
          <span>Días+: {validDayMessages[0].availableDays}</span>
          <span>Fecha de vencimiento: {firstExpirationDate}</span>
          <div className="days-plus-tooltip-divider"></div>
          <span>Periodo: {secondYear} - {secondYear - 1}</span>
          <span>Días+: {validDayMessages[1].availableDays}</span>
          <span>Fecha de vencimiento: {secondExpirationDate}</span>
        </div>
      )
    } else {
      console.warn('Hay más de dos paquetes de días+ disponibles')
      return null
    }
  }

const generateAdditionalDaysMessage = (daysMessages) => {
  const summary = getAdditionalDaysSummary(daysMessages);
  const { hasExpiringSoon, nextExpiring } = summary || {};

  return (
    <div className="additionalDaysMessagesColumn">
      {hasExpiringSoon && (
        <div>
          <div>Tienes días adicionales que vencen en los próximos 10 días, ¡no los dejes pasar!</div>
          <hr style={{ margin: "4px 0" }} />
        </div>
      )}

      <div style={{ marginBottom: "6px" }}>
        Click para ver días adicionales
      </div>
      
      {!hasExpiringSoon && nextExpiring && (
          <>
          <hr style={{ margin: "4px 0" }} />
          <div>
            <div>{`Próximo a vencer: ${nextExpiring.availableDays} días`}</div>
            <div>{`Motivo: ${nextExpiring.motive}`}</div>
            <div>{`Vencimiento: ${nextExpiring.expirationDate}`}</div>
          </div>
          </>
      )}
    </div>
  );
};

  const availableVacationDays = () => {
    const plusDays = holidaySchemaId === IDS_HOLIDAY_SCHEMA.INTERNATIONAL ? 0 : (data.availablePlusDays || 0)
    const bucketDays = holidaySchemaId === IDS_HOLIDAY_SCHEMA.INTERNATIONAL ? 0 : (data.availableBucketDays || 0)
    const accordedDays = holidaySchemaId !== IDS_HOLIDAY_SCHEMA.INTERNATIONAL ? 0 : (data.availableAccordedDays || 0)
    const additionalDays = data.availableAdditionalDays || 0
    return plusDays + Math.max(bucketDays, 0) + accordedDays + additionalDays
  }

  const showPlusDaysIcon = plusDaysMessage.length > 0 && plusDaysMessage.some(msg => msg.availableDays > 0) && !isPlusDaysPackageAboutToExpire(plusDaysMessage)

  const availableDaysLabel = screenWidth && screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT ? "Días disponibles actuales: " : "Disponibles: "
  const daysTakenLabel = screenWidth && screenWidth > BREAKPOINTS.MOBILE_BREAKPOINT ? "Días solicitados del periodo: " : "Solicitados: "

  const totalDiasPorAprobar = Number(takenDaysResume?.pendingBucketDays || 0) + 
  Number(takenDaysResume?.pendingPlusDays || 0) + 
  Number(takenDaysResume?.pendingAccordedDays || 0) + 
  Number(takenDaysResume?.pendingAdditionalDays || 0)

  const totalDiasAprobados = Number(takenDaysResume?.approvedBucketDays || 0) + 
  Number(takenDaysResume?.approvedPlusDays || 0) + 
  Number(takenDaysResume?.approvedAccordedDays || 0) + 
  Number(takenDaysResume?.approvedAdditionalDays || 0)


  const takenDaysTooltipContent = (type = VACATION_DAY_TYPE_COPYS.POR_APROBAR) => {
    const bucketDays = type === VACATION_DAY_TYPE_COPYS.POR_APROBAR ? (Number(takenDaysResume?.pendingBucketDays) || 0) : (Number(takenDaysResume?.approvedBucketDays) || 0)
    const plusDays = type === VACATION_DAY_TYPE_COPYS.POR_APROBAR ? (Number(takenDaysResume?.pendingPlusDays) || 0) : (Number(takenDaysResume?.approvedPlusDays) || 0)
    const accordedDays = type === VACATION_DAY_TYPE_COPYS.POR_APROBAR ? (Number(takenDaysResume?.pendingAccordedDays) || 0) : (Number(takenDaysResume?.approvedAccordedDays) || 0)
    const additionalDays = type === VACATION_DAY_TYPE_COPYS.POR_APROBAR ? (Number(takenDaysResume?.pendingAdditionalDays) || 0) : (Number(takenDaysResume?.approvedAdditionalDays) || 0)
    const isInternationalSchema = holidaySchemaId === IDS_HOLIDAY_SCHEMA.INTERNATIONAL
    return (
      <div>
        <p>Tipo de días:</p>
        <div className="flex gap-2">
          {!isInternationalSchema &&
            <>
              <VacationDayVariantChip
                dayType={VACATION_DAY_TYPES.BUCKET}
                quantity={bucketDays}
                variant={VACATION_DAYS_VARIANTS.LONG}
                showQuantity
              />
              <VacationDayVariantChip
                dayType={VACATION_DAY_TYPES.PLUS}
                quantity={plusDays}
                variant={VACATION_DAYS_VARIANTS.LONG}
                showQuantity
              />
            </>
          }
          {
            isInternationalSchema && (
              <VacationDayVariantChip
                dayType={VACATION_DAY_TYPES.ACCORDED}
                quantity={accordedDays}
                showQuantity
                variant={VACATION_DAYS_VARIANTS.LONG}
              />
            )
          }
          <VacationDayVariantChip
            dayType={VACATION_DAY_TYPES.EXTRA}
            quantity={additionalDays}
            variant={VACATION_DAYS_VARIANTS.LONG}
            showQuantity
          />
        </div>
    </div>
    )
  }

 const additionalDaysSummary = getAdditionalDaysSummary(additionalDaysMessages);
 const hasAdditionalExpiringSoon = additionalDaysSummary?.hasExpiringSoon;
 const AdditionalDaysIcon = hasAdditionalExpiringSoon
  ? "pi pi-exclamation-triangle text-red-500"
  : "pi pi-info-circle"; 

  return (
    <>
    <ModalForm
    visible={isModalBucketResumeVisible}
    setVisible={setIsModalBucketResumeVisible}
    headerTitle="Resumen de cubeta"
    closable
    withoutFooter
    headerIconClass="pi pi-history"
    >
      <TeamMemberVacationsBucketTable
        teamMemberId={teamMemberID}
        workAnniversary={workAnniversary}
        periodStartDate={periodRange[0].toISO()}
        periodEndDate={periodRange[1].toISO()}
      />
    </ModalForm>
    <ModalForm
    visible={isModalAditionalResumeVisible}
    setVisible={setIsModalAditionalResumeVisible}
    headerTitle="Resumen de días adicionales"
    closable
    withoutFooter
    headerIconClass="pi pi-history"
    className="aditional-days-modal"
    >
      <TeamMemberVacationsAditionalTable
        teamMemberId={teamMemberID}
        workAnniversary={workAnniversary}
        periodStartDate={periodRange[0].toISO()}
        periodEndDate={periodRange[1].toISO()}
      />
    </ModalForm>

    <div className="vacations-resume-container">
    <Card className="shadow-2 team-member-vacations-resume-card">
      <div className="flex flex-column gap-2 section">
        <span className="days-label-summary font-cairo">{daysTakenLabel}{takenDaysResume.requestedTotal || 0}</span>
        <ValueWithLabelContainer>
          <VacationDaysValueTemplate
            label={VACATION_DAY_TYPE_COPYS.POR_APROBAR}
            value={totalDiasPorAprobar}
            showZeroValue
            loading={isTakenDaysLoading}
            labelTooltip={totalDiasPorAprobar > 0}
            labelTooltipContent={takenDaysTooltipContent(VACATION_DAY_TYPE_COPYS.POR_APROBAR)}
            labelTooltipPosition="right"
            labelTooltipIcon='pi pi-info-circle'
          />
          <VacationDaysValueTemplate
            label={VACATION_DAY_TYPE_COPYS.APROBADOS}
            value={totalDiasAprobados}
            showZeroValue
            loading={isTakenDaysLoading}
            labelTooltip={totalDiasAprobados > 0}
            labelTooltipContent={takenDaysTooltipContent(VACATION_DAY_TYPE_COPYS.APROBADOS)}
            labelTooltipPosition="right"
            labelTooltipIcon='pi pi-info-circle'
          />
        </ValueWithLabelContainer>
      </div>
    </Card>
      <Card className="shadow-2 team-member-vacations-resume-card">
      <div className="flex flex-column gap-2">
        <span className="days-label-summary font-cairo">{availableDaysLabel}{availableVacationDays()}</span>
      <ValueWithLabelContainer>
        {(
          typesDayOff.length > 0 &&
          typesDayOff.find(
            (dayOff) => dayOff.type === VACATION_DAY_TYPE_NAMES.BUCKET
          )
        ) ?
          <VacationDaysValueTemplate
            label={VACATION_DAY_TYPE_COPYS.BUCKET}
            value={data.availableBucketDays}
            loading={loading}
            showWarningBadge={data.availableBucketDays < 0}
            showZeroValue
            warningBadgeTooltipContent="Click para ver resumen de cubeta"
            warningBadgeOnClick={modalBucketResumeVisibleToggle}
            labelTooltip={data.availableBucketDays > 0}
            labelTooltipContent={"Click para ver resumen de cubeta"}
            labelTooltipPosition="right"
            labelTooltipIcon='pi pi-info-circle'
            tooltipOnClick={modalBucketResumeVisibleToggle}
          />
        : null}
        {(
          typesDayOff.length > 0 &&
          typesDayOff.find(
            (dayOff) => dayOff.type === VACATION_DAY_TYPE_NAMES.PLUS
          )
        ) ?
          <VacationDaysValueTemplate
            label={VACATION_DAY_TYPE_COPYS.PLUS}
            value={data.availablePlusDays}
            loading={loading}
            labelTooltip={showPlusDaysIcon}
            labelTooltipContent={generatePlusDaysMessage(plusDaysMessage)}
            labelTooltipPosition="right"
            labelTooltipIcon={"pi pi-info-circle"}
            showWarningBadge={isPlusDaysPackageAboutToExpire(plusDaysMessage)}
            warningBadgeTooltipContent={generatePlusDaysMessage(plusDaysMessage)}
            showZeroValue
          />
        : null}
        {(
          typesDayOff.length > 0 &&
          typesDayOff.find(
            (dayOff) => dayOff.type === VACATION_DAY_TYPE_NAMES.ACCORDED
          )
        ) ?
          <VacationDaysValueTemplate
            label={VACATION_DAY_TYPE_COPYS.ACCORDED}
            value={data.availableAccordedDays}
            loading={loading}
            showZeroValue
          />
        : null}
        {(
          typesDayOff.length > 0 &&
          data.availableAdditionalDays > 0 &&
          typesDayOff.find(
            (dayOff) => dayOff.type === VACATION_DAY_TYPE_NAMES.EXTRA
          )
        ) ?
          <VacationDaysValueTemplate
            label={VACATION_DAY_TYPE_COPYS.EXTRA}
            value={data.availableAdditionalDays}
            loading={loading}
            labelTooltip={additionalDaysMessages.length > 0}
            labelTooltipContent={generateAdditionalDaysMessage(
              additionalDaysMessages
            )}
            labelTooltipPosition="bottom"
            labelTooltipIcon={AdditionalDaysIcon}
            tooltipOnClick={modalAditionalResumeVisibleToggle}
            showZeroValue
          />
        : null}
      </ValueWithLabelContainer>
      </div>
    </Card>
    </div>
    </>
  )
}

export default TeamMemberVacationsResume

TeamMemberVacationsResume.propTypes = {
  periodRange: PropTypes.array,
  setLoading: PropTypes.func,
}
